import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendSMS, formatPhoneNumber } from '../lib/twilio';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiMessageSquare, FiSend, FiLoader, FiAlertTriangle, FiCheck } = FiIcons;

function MessageModal({ isOpen, onClose, recipient = null, onSent = () => {} }) {
  const [message, setMessage] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(recipient?.phone || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Set recipient phone number when it changes
  useEffect(() => {
    if (recipient?.phone) {
      setPhoneNumber(recipient.phone);
    }
  }, [recipient]);

  // Auto close after successful send
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && result?.success) {
      handleClose();
    }
  }, [countdown, result]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || !phoneNumber.trim()) return;
    
    setSending(true);
    setResult(null);
    
    try {
      // Send SMS via Twilio
      const customerId = recipient?.type === 'customer' ? recipient.id : null;
      const leadId = recipient?.type === 'lead' ? recipient.id : null;
      
      const response = await sendSMS(phoneNumber, message, customerId, leadId);
      
      // Update state based on response
      setResult(response);
      
      // If successful, start countdown to close
      if (response.success) {
        setCountdown(3);
        onSent({
          to: phoneNumber,
          message,
          timestamp: new Date().toISOString(),
          status: response.status,
          id: response.messageId
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setResult({
        success: false,
        error: 'Failed to send message. Please try again.'
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setMessage('');
      setPhoneNumber('');
      setResult(null);
      setCountdown(0);
      onClose();
    }
  };

  const getCharacterCount = () => {
    const count = message.length;
    const segments = Math.ceil(count / 160);
    return { count, segments };
  };

  const { count, segments } = getCharacterCount();
  const isOverLimit = count > 1600; // 10 segments max

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={!sending ? handleClose : undefined}></div>
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl transform transition-all relative z-50"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Send SMS Message
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={sending}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Recipient */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipient Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="+1 (555) 123-4567"
              required
              disabled={sending || !!recipient}
            />
            {recipient && (
              <p className="text-xs text-gray-500 mt-1">
                Sending to {recipient.firstName} {recipient.lastName}
              </p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                isOverLimit ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Type your message here..."
              required
              disabled={sending}
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={isOverLimit ? 'text-red-500' : 'text-gray-500'}>
                {count} / 1600 characters
              </span>
              <span className="text-gray-500">
                {segments} {segments === 1 ? 'message' : 'messages'}
              </span>
            </div>
          </div>

          {/* Result Message */}
          {result && (
            <div className={`p-3 rounded-lg ${
              result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center">
                <SafeIcon 
                  icon={result.success ? FiCheck : FiAlertTriangle} 
                  className={`w-5 h-5 mr-2 ${result.success ? 'text-green-600' : 'text-red-600'}`}
                />
                <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                  {result.success 
                    ? `Message sent successfully! Closing in ${countdown}...` 
                    : result.error
                  }
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              disabled={sending}
            >
              Cancel
            </button>
            <motion.button
              type="submit"
              whileHover={{ scale: sending ? 1 : 1.02 }}
              whileTap={{ scale: sending ? 1 : 0.98 }}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={sending || isOverLimit || !message.trim() || !phoneNumber.trim()}
            >
              {sending ? (
                <span className="flex items-center">
                  <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin mr-2" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center">
                  <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
                  Send Message
                </span>
              )}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default MessageModal;